import { type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Modal } from '@/components/ui/modal';
import { Fade } from '@/components/ui/fade';
import { AnimatedHeight } from '@/components/ui/animated-height';
import { pb } from '@/core/api';
import { useMinLoading } from '@/hooks/use-min-loading';
import type { Approacher } from '../types';
import { ApproacherDetailContent } from './approacher-detail-content';
import { ApproacherDetailSkeleton } from './approacher-detail-skeleton';

type Props = { approacherId: string | null; onClose: () => void };

function fetchMember(id: string): Promise<Approacher> {
  return pb.collection('team_members').getOne<Approacher>(id);
}

function pickView(
  ready: boolean,
  data: Approacher | undefined,
  onClose: () => void
): ReactNode {
  if (ready && data)
    return <ApproacherDetailContent approacher={data} onClose={onClose} />;
  return <ApproacherDetailSkeleton />;
}

function Content({
  id,
  onClose,
}: {
  id: string;
  onClose: () => void;
}): ReactNode {
  const { data, isLoading } = useQuery({
    queryKey: ['approacher', id],
    queryFn: () => fetchMember(id),
    enabled: !!id,
  });
  const showSkeleton = useMinLoading(isLoading);
  const ready = !showSkeleton && !!data;
  return (
    <AnimatedHeight>
      <Fade activeKey={ready ? 'data' : 'skeleton'}>
        {pickView(ready, data, onClose)}
      </Fade>
    </AnimatedHeight>
  );
}

export function ApproacherDetailModal({
  approacherId,
  onClose,
}: Props): ReactNode {
  return (
    <Modal open={!!approacherId} onClose={onClose} title="Collector">
      {approacherId && <Content id={approacherId} onClose={onClose} />}
    </Modal>
  );
}
